import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { Animated, Easing, Pressable, StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { primitive, spacing } from 'styles/constants';

import { maybePlayAudioOnce, takeoverAudioMuted } from './play-audio-once';
import { useAppTakeover } from './use-app-takeover';

let didToggleMute = false;

export const TakeoverVolumeButton = ({ lottieFinished }: { lottieFinished: boolean }) => {
  const appTakeover = useAppTakeover();
  const { audio: takeoverAudio, muteButtonCoachmarkArgs: coachmarkArgs } =
    appTakeover?.homePage || {};

  const [isAudioMuted, setIsAudioMuted] = useState(takeoverAudioMuted.current);
  const [isCoachmarkVisible, _setIsCoachmarkVisible] = useState(false);
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const pulseAnimationRef = useRef<Animated.CompositeAnimation>(null);
  const coachmarkFadeAnim = useRef(new Animated.Value(0)).current;

  const setIsCoachmarkVisible = useCallback(
    (value: boolean) => {
      if (value) {
        // add element so it can fade in
        _setIsCoachmarkVisible(true);
      }
      Animated.timing(coachmarkFadeAnim, {
        toValue: value ? 1 : 0,
        duration: 500,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }).start(() => {
        if (!value) {
          // remove element after its faded
          _setIsCoachmarkVisible(false);
        }
      });
    },
    [coachmarkFadeAnim]
  );

  useEffect(() => {
    pulseAnimationRef.current = Animated.loop(
      Animated.timing(pulseAnim, {
        toValue: 1,
        duration: 1200,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      })
    );

    pulseAnimationRef.current.start();

    return () => {
      pulseAnimationRef.current?.stop();
    };
  }, [pulseAnim]);

  const pulseScale = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.4],
  });

  const pulseOpacity = pulseAnim.interpolate({
    inputRange: [0.5, 1],
    outputRange: [0.5, 0],
  });

  useEffect(() => {
    if (!lottieFinished || didToggleMute) {
      return;
    }
    setIsCoachmarkVisible(true);
  }, [isAudioMuted, lottieFinished, coachmarkArgs, setIsCoachmarkVisible]);

  return (
    <View style={styles.muteButtonWrapper}>
      <View style={{ position: 'relative' }}>
        {isCoachmarkVisible && coachmarkArgs ? (
          <Animated.View style={[styles.coachmarkContainer, { opacity: coachmarkFadeAnim }]}>
            <View style={styles.coachmarkBubble}>
              <Pressable
                onPress={() => setIsCoachmarkVisible(false)}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                style={styles.coachmarkCloseButton}
              >
                <Icon variant="cancel" color={primitive.$bbqBrown} size="$5" />
              </Pressable>
              {coachmarkArgs.imageSource ? (
                <Image
                  source={coachmarkArgs.imageSource}
                  style={styles.coachmarkImage}
                  contentFit="contain"
                />
              ) : null}
              <View style={styles.coachmarkTextContainer}>
                <Typography style={{ lineHeight: 25 }}>{coachmarkArgs.message}</Typography>
              </View>
            </View>
            <View style={styles.coachmarkPointerFill} pointerEvents="none" />
          </Animated.View>
        ) : null}
        {isCoachmarkVisible && (
          <Animated.View
            pointerEvents="none"
            style={[
              styles.muteButtonPulse,
              {
                opacity: pulseOpacity,
                transform: [{ scale: pulseScale }],
              },
            ]}
          />
        )}
        <Pressable
          onPress={() => {
            takeoverAudioMuted.current = !takeoverAudioMuted.current;
            logButtonClick({
              attributes: {
                component: ClickEventComponentNames.BUTTON,
                name: `${takeoverAudioMuted.current ? 'Mute' : 'Unmute'} takeover audio`,
              },
            });
            setIsAudioMuted(takeoverAudioMuted.current);
            if (!didToggleMute) {
              pulseAnimationRef.current?.stop();
              maybePlayAudioOnce(takeoverAudio, 0);
              setIsCoachmarkVisible(false);
              didToggleMute = true;
            }
          }}
          style={styles.muteButton}
          accessibilityRole="button"
          accessibilityLabel={isAudioMuted ? 'Unmute audio' : 'Mute audio'}
          aria-label={isAudioMuted ? 'Unmute audio' : 'Mute audio'}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Icon
            variant={isAudioMuted ? 'volumeOff' : 'volumeOn'}
            size="$5"
            color={isAudioMuted ? primitive.$fieryRed : primitive.$bbqBrown}
          />
        </Pressable>
      </View>
    </View>
  );
};

const BUTTON_SIZE = 44;
const POINTER_SIZE = 8;
const POINTER_OFFSET = 5;

const styles = StyleSheet.create({
  muteButtonWrapper: {
    position: 'absolute',
    right: spacing.$4,
    bottom: spacing.$4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  muteButtonPulse: {
    position: 'absolute',
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: 22,
    backgroundColor: primitive.$white,
  },
  muteButton: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: 22,
    backgroundColor: primitive.$mayoEggWhite,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: primitive.$bbqBrown,
  },
  coachmarkContainer: {
    position: 'absolute',
    right: 0,
    bottom: BUTTON_SIZE + POINTER_OFFSET + 5,
    width: 300,
  },
  coachmarkBubble: {
    flexDirection: 'row',
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$3,
    gap: spacing.$2,
    backgroundColor: primitive.$mayoEggWhite,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.45,
    shadowRadius: 3.84,
    elevation: 5,
  },
  coachmarkCloseButton: {
    position: 'absolute',
    right: 10,
    top: 10,
    zIndex: 1,
  },
  coachmarkImage: {
    flex: 1,
  },
  coachmarkTextContainer: {
    flex: 5,
  },
  coachmarkPointerFill: {
    position: 'absolute',
    right: POINTER_SIZE + POINTER_OFFSET,
    bottom: -POINTER_OFFSET,
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderTopWidth: POINTER_SIZE,
    borderTopColor: primitive.$mayoEggWhite,
    borderLeftWidth: POINTER_SIZE + 2,
    borderLeftColor: 'transparent',
    borderRightWidth: POINTER_SIZE + 2,
    borderRightColor: 'transparent',
  },
});
